import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { AsyncSearchableSelect, type SelectOption } from './AsyncSearchableSelect';
import { Button } from './ui/button';
import { NumberInput } from '@/components/ui/number-input';
import {
  getPlaceEquipmentSummary,
  addPlaceEquipment,
  removePlaceEquipment,
  listPlaceEquipment,
  listThings,
  type PlaceEquipmentSummary,
  type PlaceEquipmentCreate,
  type ThingWithPlaceContext,
} from '@littlebooker/dal';

interface PlaceEquipmentManagerProps {
  placeId: string;
  placeName: string;
}

export function PlaceEquipmentManager({ placeId, placeName }: PlaceEquipmentManagerProps) {
  const queryClient = useQueryClient();
  const [selectedThingId, setSelectedThingId] = useState<string | null>(null);
  const [availabilityType, setAvailabilityType] = useState<'default' | 'on_request'>('default');
  const [quantity, setQuantity] = useState<number>(1);

  // Fetch equipment summary
  const { data: summary, isLoading } = useQuery<PlaceEquipmentSummary>({
    queryKey: ['place-equipment-summary', placeId],
    queryFn: () => getPlaceEquipmentSummary(placeId),
  });

  // Get IDs of things already assigned to this place
  const assignedThingIds = [
    ...(summary?.default_equipment.map((e) => e.id) || []),
    ...(summary?.on_request_equipment.map((e) => e.id) || []),
  ];

  // Search function for AsyncSearchableSelect
  const searchThings = async (query: string): Promise<SelectOption[]> => {
    try {
      const things = await listThings({ query, status: 'available' });

      // Filter out already assigned equipment
      const filtered = things.filter((thing) => !assignedThingIds.includes(thing.id));

      return filtered.map((thing) => ({
        id: thing.id,
        name: thing.name,
        subtitle: thing.type,
      }));
    } catch (error) {
      console.error('Error searching things:', error);
      return [];
    }
  };

  // Add equipment mutation
  const addMutation = useMutation({
    mutationFn: (data: PlaceEquipmentCreate) => addPlaceEquipment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place-equipment-summary', placeId] });
      setSelectedThingId(null);
      setQuantity(1);
    },
  });

  // Remove equipment mutation
  const removeMutation = useMutation({
    mutationFn: (equipmentId: string) => removePlaceEquipment(equipmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place-equipment-summary', placeId] });
    },
  });

  const handleAddEquipment = () => {
    if (!selectedThingId) return;

    addMutation.mutate({
      place_id: placeId,
      thing_id: selectedThingId,
      availability_type: availabilityType,
      default_quantity: quantity,
    });
  };

  const handleRemoveEquipment = async (equipment: ThingWithPlaceContext) => {
    if (!confirm(`Remove ${equipment.name} from this place?`)) return;

    // We need to get the place_equipment ID to delete
    // For now, we'll fetch it from the full equipment list
    try {
      const fullList = await listPlaceEquipment(placeId);
      const equipmentEntry = fullList.find((pe) => pe.thing_id === equipment.id);

      if (equipmentEntry) {
        removeMutation.mutate(equipmentEntry.id);
      }
    } catch (error) {
      console.error('Error removing equipment:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-2">Equipment at {placeName}</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Manage what equipment is available at this location. Default equipment is always here,
          on-request equipment can be brought to this location when needed.
        </p>
      </div>

      {/* Add Equipment Section */}
      <div className="border rounded-lg p-4 space-y-3">
        <h4 className="font-medium text-sm">Add Equipment</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="text-xs text-muted-foreground mb-1 block">Equipment</label>
            <AsyncSearchableSelect
              placeholder="Search equipment..."
              searchFn={searchThings}
              onSelect={(option) => setSelectedThingId(option.id)}
              selectedIds={selectedThingId ? [selectedThingId] : []}
              minSearchLength={1}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Type</label>
            <select
              value={availabilityType}
              onChange={(e) => setAvailabilityType(e.target.value as 'default' | 'on_request')}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              <option value="default">Default (Always here)</option>
              <option value="on_request">On Request</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Quantity</label>
            <NumberInput
              min={1}
              allowEmpty={false}
              value={quantity}
              onChange={(n) => setQuantity(n ?? 1)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
        <Button
          onClick={handleAddEquipment}
          disabled={!selectedThingId || addMutation.isPending}
          size="sm"
        >
          {addMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              <Plus className="mr-2 h-4 w-4" />
              Add Equipment
            </>
          )}
        </Button>
      </div>

      {/* Default Equipment List */}
      <div className="space-y-2">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-success"></span>
          Default Equipment ({summary?.default_equipment.length || 0})
        </h4>
        <div className="border rounded-lg divide-y">
          {summary?.default_equipment.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No default equipment assigned
            </div>
          ) : (
            summary?.default_equipment.map((equipment) => (
              <div key={equipment.id} className="p-3 flex items-center justify-between gap-3">
                {equipment.image_url && (
                  <img
                    src={equipment.image_url}
                    alt={equipment.name}
                    className="h-12 w-12 rounded object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1">
                  <div className="font-medium">{equipment.name}</div>
                  <div className="text-xs text-muted-foreground">
                    Qty: {equipment.place_equipment?.default_quantity || 1}
                    {equipment.place_equipment?.notes && ` • ${equipment.place_equipment.notes}`}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveEquipment(equipment)}
                  disabled={removeMutation.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* On Request Equipment List */}
      <div className="space-y-2">
        <h4 className="font-medium text-sm flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded-full bg-info"></span>
          On Request Equipment ({summary?.on_request_equipment.length || 0})
        </h4>
        <div className="border rounded-lg divide-y">
          {summary?.on_request_equipment.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground text-center">
              No on-request equipment assigned
            </div>
          ) : (
            summary?.on_request_equipment.map((equipment) => (
              <div key={equipment.id} className="p-3 flex items-center justify-between gap-3">
                {equipment.image_url && (
                  <img
                    src={equipment.image_url}
                    alt={equipment.name}
                    className="h-12 w-12 rounded object-cover flex-shrink-0"
                  />
                )}
                <div className="flex-1">
                  <div className="font-medium">{equipment.name}</div>
                  <div className="text-xs text-muted-foreground">
                    Max qty: {equipment.place_equipment?.default_quantity || 1}
                    {equipment.place_equipment?.notes && ` • ${equipment.place_equipment.notes}`}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveEquipment(equipment)}
                  disabled={removeMutation.isPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
